import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Receipt, Calendar as CalendarIcon, Search } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COST_CATEGORIES = {
  staff_salaries: {
    label: "Staff Salaries",
    subcategories: ["Monthly Wages", "Overtime Pay", "Bonuses", "Other"]
  },
  raw_materials: {
    label: "Raw Materials",
    subcategories: ["Flour", "Beef", "Sausage", "Sugar", "Eggs", "Margarine", "Ground Nut Oil", "Baking Soda", "Other"]
  },
  utilities: {
    label: "Utilities",
    subcategories: ["Gas", "Electricity", "Water", "Other"]
  },
  fuel: {
    label: "Fuel",
    subcategories: ["Diesel (Genset)", "Petrol (Delivery)", "Other"]
  },
  vehicle_maintenance: {
    label: "Vehicle Maintenance",
    subcategories: ["Repairs", "Servicing", "Parts Replacement", "Other"]
  },
  miscellaneous: {
    label: "Miscellaneous",
    subcategories: ["Space Rentals", "Utensils Replacement", "Packaging", "Cleaning Supplies", "Other"]
  }
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

export default function Costs() {
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [filterCategory, setFilterCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Form state
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [costDate, setCostDate] = useState(new Date());

  useEffect(() => {
    fetchCosts();
  }, []);

  const fetchCosts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/costs`);
      setCosts(response.data);
    } catch (error) {
      toast.error("Failed to fetch costs");
    } finally {
      setLoading(false);
    }
  };

  const handleAddCost = async () => {
    if (!category || !subcategory || !amount || !costDate) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      await axios.post(`${API}/costs`, {
        category,
        subcategory,
        description: description || subcategory,
        amount: parseFloat(amount),
        cost_date: costDate.toISOString()
      });
      toast.success("Cost recorded successfully");
      setIsDialogOpen(false);
      resetForm();
      fetchCosts();
    } catch (error) {
      toast.error("Failed to record cost");
    }
  };

  const handleDeleteCost = async (costId) => {
    try {
      await axios.delete(`${API}/costs/${costId}`);
      toast.success("Cost deleted");
      fetchCosts();
    } catch (error) {
      toast.error("Failed to delete cost");
    }
  };

  const resetForm = () => {
    setCategory("");
    setSubcategory("");
    setDescription("");
    setAmount("");
    setCostDate(new Date());
  };

  // Get subcategories for selected category
  const availableSubcategories = category ? COST_CATEGORIES[category]?.subcategories || [] : [];

  // Filter costs
  const filteredCosts = costs.filter(cost => {
    const matchesCategory = filterCategory === "all" || cost.category === filterCategory;
    const matchesSearch = 
      cost.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cost.subcategory.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Calculate totals
  const totalCosts = filteredCosts.reduce((sum, cost) => sum + cost.amount, 0);

  // Group costs by category for summary
  const costsByCategory = costs.reduce((acc, cost) => {
    if (!acc[cost.category]) acc[cost.category] = 0;
    acc[cost.category] += cost.amount;
    return acc;
  }, {});

  return (
    <div className="space-y-8 animate-fade-in" data-testid="costs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 tracking-tight">Costs</h1>
          <p className="text-stone-500 mt-1">Track all your business expenses</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="add-cost-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Cost
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="add-cost-dialog">
            <DialogHeader>
              <DialogTitle>Record New Cost</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={category} onValueChange={(val) => { setCategory(val); setSubcategory(""); }}>
                  <SelectTrigger data-testid="cost-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(COST_CATEGORIES).map(([key, { label }]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Subcategory</Label>
                <Select value={subcategory} onValueChange={setSubcategory} disabled={!category}>
                  <SelectTrigger data-testid="cost-subcategory-select">
                    <SelectValue placeholder="Select subcategory" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableSubcategories.map((sub) => (
                      <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Description (optional)</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Add more details..."
                  data-testid="cost-description-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Amount (NGN)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter amount"
                  data-testid="cost-amount-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="cost-date-picker"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {costDate ? format(costDate, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={costDate}
                      onSelect={setCostDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <Button 
                onClick={handleAddCost} 
                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="submit-cost-btn"
              >
                Add Cost
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Category Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {Object.entries(COST_CATEGORIES).map(([key, { label }]) => (
          <Card 
            key={key} 
            className={`cursor-pointer transition-all ${filterCategory === key ? 'ring-2 ring-amber-500' : ''}`}
            onClick={() => setFilterCategory(filterCategory === key ? "all" : key)}
            data-testid={`category-card-${key}`}
          >
            <CardContent className="p-4">
              <p className="text-xs text-stone-500 truncate">{label}</p>
              <p className="text-lg font-bold text-stone-900 mt-1">
                {formatCurrency(costsByCategory[key] || 0)}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Total Cost Card */}
      <Card className="bg-red-50 border-red-100">
        <CardContent className="p-6 flex items-center gap-4">
          <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
            <Receipt className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <p className="text-sm text-stone-500">
              {filterCategory === "all" ? "Total Costs" : `Total ${COST_CATEGORIES[filterCategory]?.label}`}
            </p>
            <p className="text-2xl font-bold text-stone-900" data-testid="total-costs-value">
              {formatCurrency(totalCosts)}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input
            placeholder="Search by description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="costs-search-input"
          />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-48" data-testid="costs-filter-select">
            <SelectValue placeholder="Filter by category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {Object.entries(COST_CATEGORIES).map(([key, { label }]) => (
              <SelectItem key={key} value={key}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Costs Table */}
      <Card data-testid="costs-table">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-stone-500">Loading...</div>
          ) : filteredCosts.length === 0 ? (
            <div className="p-8 text-center text-stone-500">
              No costs recorded yet. Click "Add Cost" to record an expense.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Subcategory</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCosts.map((cost) => (
                  <TableRow key={cost.id} className="table-row-hover" data-testid={`cost-row-${cost.id}`}>
                    <TableCell className="font-medium">
                      {new Date(cost.cost_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <span className="badge badge-warning">
                        {COST_CATEGORIES[cost.category]?.label || cost.category}
                      </span>
                    </TableCell>
                    <TableCell>{cost.subcategory}</TableCell>
                    <TableCell className="max-w-xs truncate">{cost.description}</TableCell>
                    <TableCell className="text-right font-semibold text-red-600">
                      {formatCurrency(cost.amount)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteCost(cost.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-cost-${cost.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
